import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Gauge, Loader2, RotateCcw, Search, Smartphone } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useApi } from '../../hooks/useApi';
import { cn } from '../../lib/utils';

const DEFAULT_SPEED_LIMIT = 60;

const normalizeVehicleRow = (vehicle, settingsByVehicleId = {}) => {
  const id = vehicle?.vehicle_id || vehicle?.id || vehicle?._id;
  const setting = settingsByVehicleId[id] || {};

  return {
    id,
    vehicleNumber: vehicle?.vehicle_number || vehicle?.vehicleNumber || 'N/A',
    vehicleName: vehicle?.vehicle_name || vehicle?.vehicleName || 'N/A',
    imei: vehicle?.imei_number || vehicle?.imeiNumber || vehicle?.imei || '',
    deviceId: vehicle?.device_id || vehicle?.deviceId || null,
    speedLimitKmh:
      setting.speed_limit_kmh ??
      setting.speed_limit ??
      vehicle?.speed_limit_kmh ??
      vehicle?.speed_limit ??
      DEFAULT_SPEED_LIMIT,
    enabled:
      setting.enabled ??
      setting.speed_alert_enabled ??
      vehicle?.speed_alert_enabled ??
      false,
  };
};

const buildSettingsMap = (settingsPayload) => {
  const list = Array.isArray(settingsPayload?.data)
    ? settingsPayload.data
    : Array.isArray(settingsPayload)
      ? settingsPayload
      : [];

  return list.reduce((acc, item) => {
    const vehicleId =
      item?.vehicle_id || item?.vehicleId || item?.id || item?._id;
    if (vehicleId) acc[vehicleId] = item;
    return acc;
  }, {});
};

export default function SpeedAlertSettings() {
  const { get, put } = useApi();
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savingIds, setSavingIds] = useState(() => new Set());

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const vehiclesResponse = await get('/customer/my-vehicles?per_page=1000&page=1');
      const vehicles = Array.isArray(vehiclesResponse?.data)
        ? vehiclesResponse.data
        : Array.isArray(vehiclesResponse)
          ? vehiclesResponse
          : [];

      let settingsByVehicleId = {};
      try {
        const settingsResponse = await get('/customer/speed-alert-settings');
        settingsByVehicleId = buildSettingsMap(settingsResponse);
      } catch {
        settingsByVehicleId = {};
      }

      const normalized = vehicles
        .map((vehicle) => normalizeVehicleRow(vehicle, settingsByVehicleId))
        .filter((row) => row.id);

      setRows(normalized);
      setDrafts(
        normalized.reduce((acc, row) => {
          acc[row.id] = {
            speedLimitKmh: String(row.speedLimitKmh ?? DEFAULT_SPEED_LIMIT),
            enabled: Boolean(row.enabled),
          };
          return acc;
        }, {})
      );
    } catch (error) {
      toast.error(error?.message || 'Unable to load speed alert settings.');
      setRows([]);
      setDrafts({});
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [get]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadSettings();
  };

  const updateDraft = (vehicleId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [vehicleId]: {
        ...prev[vehicleId],
        ...patch,
      },
    }));
  };

  const saveSetting = async (vehicleId) => {
    const draft = drafts[vehicleId];
    if (!draft) return;

    const speedLimit = Number.parseInt(String(draft.speedLimitKmh).trim(), 10);
    if (!Number.isFinite(speedLimit) || speedLimit < 1 || speedLimit > 300) {
      toast.error('Enter a valid speed limit between 1 and 300 km/h.');
      return;
    }

    setSavingIds((prev) => new Set(prev).add(vehicleId));
    const payload = {
      speed_limit_kmh: speedLimit,
      enabled: Boolean(draft.enabled),
    };

    try {
      try {
        await put(`/customer/speed-alert-settings/${vehicleId}`, payload);
      } catch {
        await put(`/customer/my-vehicles/${vehicleId}`, {
          speed_limit_kmh: payload.speed_limit_kmh,
          speed_alert_enabled: payload.enabled,
        });
      }

      setRows((prev) =>
        prev.map((row) =>
          row.id === vehicleId
            ? { ...row, speedLimitKmh: speedLimit, enabled: payload.enabled }
            : row
        )
      );
      toast.success('Speed alert settings saved.');
    } catch (error) {
      toast.error(error?.message || 'Failed to save speed alert settings.');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(vehicleId);
        return next;
      });
    }
  };

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.trim().toLowerCase();
    return rows.filter(
      (row) =>
        row.vehicleName.toLowerCase().includes(term) ||
        row.vehicleNumber.toLowerCase().includes(term) ||
        row.imei.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const showEmptyState = !isLoading && filteredRows.length === 0;

  const panelClass =
    'overflow-hidden rounded-xl bg-card text-card-foreground shadow-sm dark:bg-[#18181b]';
  const mobileShellClass = 'block md:hidden space-y-2.5';
  const panelToolbarClass = 'flex items-center gap-2 px-0 py-2.5 sm:px-4';
  const searchWrapClass = 'relative min-w-0 flex-1';
  const searchInputClass =
    'h-10 w-full rounded-full border-0 bg-muted pl-10 pr-4 text-[13px] text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-[#27272a] dark:text-zinc-100 dark:placeholder:text-zinc-500';
  const toolbarButtonClass =
    'h-10 w-10 shrink-0 rounded-full border-0 bg-muted p-0 hover:bg-muted/80 dark:bg-[#27272a] dark:hover:bg-[#2f2f33]';
  const gridHeaderRowClass = 'border-0 hover:bg-transparent';
  const gridHeadClass =
    'h-9 px-3 py-0 text-[12px] font-medium text-muted-foreground select-none whitespace-nowrap';
  const gridCellClass =
    'px-3 py-1.5 text-[12px] leading-tight text-foreground align-middle whitespace-nowrap';
  const gridRowBaseClass =
    'h-9 border-0 transition-colors hover:bg-muted/50 dark:hover:bg-[#27272a]';
  const getGridRowClass = (index) =>
    cn(
      gridRowBaseClass,
      index % 2 === 1
        ? 'bg-muted/40 dark:bg-[#1f1f23]'
        : 'bg-background/50 dark:bg-[#18181b]'
    );
  const compactTableClass =
    'min-w-full text-xs [&_td]:p-0 [&_th]:p-0 [&_tr]:h-9 [&_tr]:border-0';
  const mobileCardClass =
    'overflow-hidden rounded-xl border border-border/50 bg-card p-3.5 shadow-sm dark:border-zinc-800/80 dark:bg-[#1f1f23]';
  const mobileCardInsetClass = 'rounded-lg bg-muted/70 px-2.5 py-2 dark:bg-[#27272a]';
  const mobileListClass = 'space-y-2.5';
  const saveButtonClass =
    'h-8 rounded-lg bg-muted px-3 text-[12px] font-medium text-foreground hover:bg-muted/80 dark:bg-[#27272a] dark:text-zinc-100 dark:hover:bg-[#2f2f33]';

  const renderSearchToolbar = () => (
    <div className={panelToolbarClass}>
      <div className={searchWrapClass}>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={searchInputClass}
        />
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleRefresh}
        className={toolbarButtonClass}
        disabled={isRefreshing}
        aria-label="Refresh speed alert settings"
      >
        <RotateCcw
          className={cn('h-4 w-4 text-zinc-500 dark:text-zinc-400', isRefreshing && 'animate-spin')}
        />
      </Button>
    </div>
  );

  const renderSettingControls = (row, compact = false) => {
    const draft = drafts[row.id] || {
      speedLimitKmh: String(row.speedLimitKmh ?? DEFAULT_SPEED_LIMIT),
      enabled: Boolean(row.enabled),
    };
    const isSaving = savingIds.has(row.id);

    return (
      <div className={cn('flex items-center gap-2', compact && 'flex-wrap')}>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={300}
            inputMode="numeric"
            value={draft.speedLimitKmh}
            onChange={(e) => updateDraft(row.id, { speedLimitKmh: e.target.value })}
            className={cn(
              'h-8 border-0 bg-muted text-[12px] dark:bg-[#27272a]',
              compact ? 'w-20' : 'w-24'
            )}
            aria-label={`Speed limit for ${row.vehicleName}`}
          />
          <span className="text-[11px] text-muted-foreground">km/h</span>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={draft.enabled}
            onCheckedChange={(checked) => updateDraft(row.id, { enabled: checked })}
            aria-label={`Enable speed alerts for ${row.vehicleName}`}
          />
          <span className="text-[11px] text-muted-foreground">
            {draft.enabled ? 'On' : 'Off'}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={saveButtonClass}
          disabled={isSaving}
          onClick={() => saveSetting(row.id)}
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              Saving
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    );
  };

  const MobileSettingCard = ({ row }) => (
    <article className={mobileCardClass}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Vehicle
          </p>
          <p className="truncate text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
            {row.vehicleName}
          </p>
          <p className="truncate text-[11px] text-zinc-600 dark:text-zinc-400">
            {row.vehicleNumber}
          </p>
        </div>
        <Gauge className="h-4 w-4 shrink-0 text-violet-500 dark:text-violet-400" />
      </div>

      {row.imei ? (
        <div className={`mb-3 flex items-start gap-2 ${mobileCardInsetClass}`}>
          <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500 dark:text-zinc-400" />
          <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              IMEI
            </p>
            <p className="break-all font-mono text-[10px] leading-snug text-zinc-700 dark:text-zinc-300">
              {row.imei}
            </p>
          </div>
        </div>
      ) : null}

      <div className={mobileCardInsetClass}>{renderSettingControls(row, true)}</div>
    </article>
  );

  const MobileSettingCardSkeleton = () => (
    <div className={mobileCardClass}>
      <Skeleton className="mb-3 h-4 w-28" />
      <Skeleton className="mb-3 h-12 rounded-lg" />
      <Skeleton className="h-10 rounded-lg" />
    </div>
  );

  const SkeletonRow = ({ index = 0 }) => (
    <TableRow className={getGridRowClass(index)}>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-24" />
      </TableCell>
      <TableCell className={`${gridCellClass} hidden sm:table-cell`}>
        <Skeleton className="h-3.5 w-28" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-8 w-40" />
      </TableCell>
    </TableRow>
  );

  return (
    <div className="relative w-full max-w-full overflow-hidden">
      <div className="mb-3">
        <h1 className="text-lg font-bold text-zinc-900 sm:text-xl dark:text-zinc-100">
          Speed Alert Settings
        </h1>
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          Set a speed limit for each vehicle and get notified when it is exceeded.
        </p>
      </div>

      <div className={mobileShellClass}>
        {renderSearchToolbar()}
        <div className={mobileListClass}>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <MobileSettingCardSkeleton key={`mobile-skeleton-${index}`} />
            ))
          ) : showEmptyState ? (
            <div className={`${mobileCardClass} py-12 text-center`}>
              <Gauge className="mx-auto mb-4 h-10 w-10 text-zinc-400 dark:text-zinc-500" />
              <h3 className="mb-2 text-[15px] font-medium text-zinc-900 dark:text-zinc-100">
                No vehicles found
              </h3>
              <p className="text-[12px] text-zinc-500 dark:text-zinc-400">
                {searchTerm
                  ? 'Try adjusting your search.'
                  : 'Add a vehicle first to configure speed alerts.'}
              </p>
            </div>
          ) : (
            filteredRows.map((row) => <MobileSettingCard key={row.id} row={row} />)
          )}
        </div>
      </div>

      <div className={`hidden md:block ${panelClass}`}>
        <div className="px-3 py-2.5 sm:px-4">{renderSearchToolbar()}</div>
        <div className="overflow-x-auto">
          <Table className={compactTableClass}>
            <TableHeader className="[&_tr]:border-0">
              <TableRow className={gridHeaderRowClass}>
                <TableHead className={`${gridHeadClass} min-w-[140px]`}>Vehicle</TableHead>
                <TableHead className={`${gridHeadClass} hidden min-w-[140px] sm:table-cell`}>
                  IMEI
                </TableHead>
                <TableHead className={`${gridHeadClass} min-w-[280px]`}>Speed Alert</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} index={index} />
                ))
              ) : showEmptyState ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-12 text-center">
                    <Gauge className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
                    <h3 className="mb-2 text-[15px] font-medium">No vehicles found</h3>
                    <p className="text-[13px] text-muted-foreground">
                      {searchTerm
                        ? 'Try adjusting your search.'
                        : 'Add a vehicle first to configure speed alerts.'}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => (
                  <TableRow key={row.id} className={getGridRowClass(index)}>
                    <TableCell className={gridCellClass}>
                      <div className="max-w-[160px]">
                        <p className="truncate font-medium">{row.vehicleName}</p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {row.vehicleNumber}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell
                      className={`${gridCellClass} hidden font-mono text-muted-foreground sm:table-cell`}
                    >
                      {row.imei || '—'}
                    </TableCell>
                    <TableCell className={gridCellClass}>{renderSettingControls(row)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
